import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listRiskControls,
  unlinkControlFromRisk,
  linkControlToRisk,
} from "../api/controls.api.js";
import LinkControlModal from "./LinkControlModal";
import Button from "../ui/Button";
import Table from "../ui/Table";
import { spacing, typography } from "../ui/tokens";
import DestructiveConfirm from "./common/DestructiveConfirm";
import ControlEffectivenessBadge from "./controls/ControlEffectivenessBadge";
import { useControlEffectivenessConfig } from "../hooks/useControlEffectivenessConfig";

const statusBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "2px 8px",
  fontSize: typography.size.xs,
  fontWeight: typography.weight.medium,
  background: "#f1f5f9",
  color: "#334155",
};

const neutralBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "2px 8px",
  fontSize: typography.size.xs,
  fontWeight: typography.weight.medium,
  background: "#f3f4f6",
  color: "#4b5563",
};

const overdueBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "2px 8px",
  fontSize: typography.size.xs,
  fontWeight: typography.weight.medium,
  background: "#fee2e2",
  color: "#b91c1c",
};

const normalizeText = (value) => String(value || "").trim();

const resolveOwnerLabel = (control, usersById, usersByUsername) => {
  const ownerUserId = control?.owner_user_id;
  if (ownerUserId != null && ownerUserId !== "") {
    const byId = usersById.get(String(ownerUserId));
    if (byId) return byId;
  }
  const ownerId = normalizeText(control?.owner_id);
  if (ownerId) {
    return usersByUsername.get(ownerId) || ownerId;
  }
  return "Unassigned";
};

export default function LinkedControlsPanel({
  riskId,
  refreshKey = 0,
  linkTrigger = 0,
  users = [],
  formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "-"),
  onOpenControl,
  onControlsChange,
}) {
  const { items: effectivenessConfig } = useControlEffectivenessConfig();
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(null);
  const navigate = useNavigate();

  const usersById = useMemo(() => {
    const map = new Map();
    (users || []).forEach((user) => {
      const id = String(user?.id || "").trim();
      const username = String(user?.username || user?.value || "").trim();
      if (id && username) map.set(id, username);
    });
    return map;
  }, [users]);

  const usersByUsername = useMemo(() => {
    const map = new Map();
    (users || []).forEach((user) => {
      const username = String(user?.username || user?.value || "").trim();
      if (username) map.set(username, username);
    });
    return map;
  }, [users]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await listRiskControls(riskId);
      const nextControls = Array.isArray(res?.data) ? res.data : [];
      setControls(nextControls);
      if (typeof onControlsChange === "function") {
        onControlsChange(nextControls);
      }
    } catch (err) {
      setError(err.message || "Failed to load controls");
      if (typeof onControlsChange === "function") {
        onControlsChange([]);
      }
    } finally {
      setLoading(false);
    }
  }, [onControlsChange, riskId]);

  useEffect(() => {
    if (riskId) {
      load();
    }
  }, [riskId, refreshKey, load]);

  useEffect(() => {
    const refreshRelationship = (event) => {
      if (String(event.detail?.riskId) === String(riskId)) load();
    };
    window.addEventListener("control-risk-links-changed", refreshRelationship);
    return () => window.removeEventListener("control-risk-links-changed", refreshRelationship);
  }, [riskId, load]);

  useEffect(() => {
    if (linkTrigger > 0) {
      setModalOpen(true);
    }
  }, [linkTrigger]);

  const handleUnlink = useCallback(
    async (controlId) => {
      try {
        await unlinkControlFromRisk(controlId, riskId);
        load();
      } catch (err) {
        setError(err.message || "Failed to unlink control");
      }
    },
    [load, riskId]
  );

  const handleOpenControl = useCallback(
    (control) => {
      if (!control?.id) return;
      if (typeof onOpenControl === "function") {
        onOpenControl(control);
        return;
      }
      navigate(`/controls/${control.id}`);
    },
    [navigate, onOpenControl]
  );

  const rows = useMemo(
    () =>
      (controls || []).map((control) => {
        const nextTestDueValue = control?.next_test_due ? new Date(control.next_test_due) : null;
        const isOverdue = nextTestDueValue && !Number.isNaN(nextTestDueValue.getTime()) && nextTestDueValue < new Date();
        return {
          ...control,
          ownerLabel: resolveOwnerLabel(control, usersById, usersByUsername),
          isOverdue,
        };
      }),
    [controls, usersById, usersByUsername]
  );

  const columns = useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        render: (control) => (
          <Button
            type="button"
            onClick={() => handleOpenControl(control)}
            variant="ghost"
            style={{
              background: "none",
              border: "none",
              padding: 0,
              margin: 0,
              color: "#2563eb",
              cursor: "pointer",
              textDecoration: "underline",
              textAlign: "left",
              boxShadow: "none",
              fontSize: typography.size.sm,
            }}
          >
            {control.name || "-"}
          </Button>
        ),
      },
      {
        id: "status",
        header: "Status",
        render: (control) => <span style={statusBadgeStyle}>{normalizeText(control.status) || "Unknown"}</span>,
      },
      {
        id: "owner",
        header: "Owner",
        accessor: (control) => control.ownerLabel || "Unassigned",
      },
      {
        id: "effectiveness",
        header: "Effectiveness",
        render: (control) => <ControlEffectivenessBadge value={control.effectiveness_rating} config={effectivenessConfig} />,
      },
      {
        id: "last_reviewed",
        header: "Last Reviewed",
        accessor: (control) => formatDate(control.last_review_date),
      },
      {
        id: "next_test_due",
        header: "Next Test Due",
        accessor: (control) => formatDate(control.next_test_due),
      },
      {
        id: "test_frequency",
        header: "Test Frequency",
        accessor: (control) => normalizeText(control.test_frequency) || "-",
      },
      {
        id: "overdue",
        header: "Overdue",
        render: (control) =>
          control.isOverdue ? <span style={overdueBadgeStyle}>Overdue</span> : <span style={neutralBadgeStyle}>No</span>,
      },
      {
        id: "actions",
        header: "",
        align: "right",
        render: (control) => (
          <>
            <Button
              onClick={() => setConfirmUnlink({ id: control.id, label: control.name || `Control #${control.id}` })}
              variant="secondary"
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                border: "1px solid #d1d5db",
                background: "white",
              }}
            >
              Remove
            </Button>
            {confirmUnlink?.id === control.id && (
              <DestructiveConfirm
                actionId="CONTROL_LINK_UNLINK"
                open
                resourceDisplayName={confirmUnlink.label}
                variantOverride="inline"
                onCancel={() => setConfirmUnlink(null)}
                onConfirm={async () => {
                  const pending = confirmUnlink;
                  setConfirmUnlink(null);
                  if (!pending) return undefined;
                  await handleUnlink(pending.id);
                  return async () => {
                    try {
                      await linkControlToRisk(pending.id, riskId);
                      load();
                    } catch (err) {
                      setError(err?.message || "Failed to relink control");
                    }
                  };
                }}
              />
            )}
          </>
        ),
      },
    ],
    [confirmUnlink, effectivenessConfig, formatDate, handleOpenControl, handleUnlink, load, riskId]
  );

  return (
    <div className="linked-controls-panel">
      {loading && <div>Loading...</div>}
      {error && <div style={{ color: "red" }}>{error}</div>}

      {!loading && !error && (
        <Table
          columns={columns}
          data={rows}
          getRowKey={(row) => row.id}
          emptyState="No controls linked yet."
          tableStyle={{ fontSize: typography.size.sm }}
          style={{ borderRadius: 8 }}
        />
      )}

      {modalOpen && (
        <LinkControlModal
          riskId={riskId}
          onClose={() => setModalOpen(false)}
          onLinked={() => {
            setModalOpen(false);
            load();
          }}
        />
      )}
      <div style={{ height: spacing.xs }} />
    </div>
  );
}
